// Track 4.3 — vision-advanced client.
//
// Sends a downsampled JPEG keyframe at ~0.5 Hz to the GPU-backed SMPL
// fitter and exposes the latest SMPLFit result to React via callback.
// One WS per session; reconnects with backoff.
//
// Keyframe cadence is deliberately low — SMPL fitting on A10G runs at
// ~25-40 ms but the network round-trip + WebSocket framing dominates,
// and the rotating avatar doesn't need 30 fps to feel alive. 0.5 Hz =
// 1 frame every 2s, plus a "ping" on position change so the avatar
// snaps into the new pose immediately.

export interface SMPLFitResult {
  keyframe_id: string;
  betas: number[];
  thetas: number[];
  mesh_url: string | null;
  processing_time_ms: number;
  /** Wave 6.2 — normalized [0..1] bbox of the detected rider, returned by
   *  the real (4D-Humans) backend. Null on the stub. AvatarOverlay uses
   *  this to align the digital twin with the rider's silhouette in the
   *  source frame. */
  bbox_norm?: [number, number, number, number] | null;
  /** Wave 6.2 — root translation (x, y, z) in metres relative to camera.
   *  Used by AvatarOverlay to place the avatar at the right depth. */
  root_translation?: [number, number, number] | null;
  /** Wave 7.1 — per-rider scaled bone offsets (metres, [j][xyz]). When
   *  present, AvatarPanel/AvatarOverlay use these instead of the constant
   *  rest-pose offsets so the avatar matches the rider's actual height +
   *  proportions (shoulder/hip width, limb length). Stabilized via EMA
   *  across frames so it doesn't twitch frame-to-frame. */
  bone_offsets?: Array<[number, number, number]> | null;
  /** Wave 7.1 — rider's measured height in metres (head top to ankle
   *  midpoint, derived from MediaPipe world landmarks + EMA). AvatarOverlay
   *  uses this to size the avatar to match the rider's silhouette. */
  rider_height_m?: number | null;
}

interface Options {
  wsUrl: string;
  userId?: string;
  sessionId?: string;
  enabled?: boolean;
  onFit?: (fit: SMPLFitResult) => void;
  onError?: (err: string) => void;
}

export class VisionAdvancedClient {
  private wsUrl: string;
  private userId: string;
  private sessionId: string;
  private enabled: boolean;
  private onFit?: (fit: SMPLFitResult) => void;
  private onError?: (err: string) => void;

  private ws: WebSocket | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private closed = false;
  private nextKeyframeId = 0;

  constructor(opts: Options) {
    this.wsUrl = opts.wsUrl;
    this.userId = opts.userId ?? '';
    this.sessionId = opts.sessionId ?? '';
    this.enabled = opts.enabled ?? true;
    this.onFit = opts.onFit;
    this.onError = opts.onError;
  }

  connect(): void {
    if (this.closed || !this.enabled) return;
    try {
      const ws = new WebSocket(this.wsUrl);
      this.ws = ws;
      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          if (msg.type === 'smpl_fit') {
            this.onFit?.(msg as SMPLFitResult);
          } else if (msg.type === 'error') {
            this.onError?.(msg.content || 'vision-advanced error');
          }
        } catch (err) {
          console.warn('vision-advanced parse error', err);
        }
      };
      ws.onerror = () => {
        this.onError?.('vision-advanced connection error');
      };
      ws.onclose = () => {
        if (this.closed) return;
        this.reconnectTimer = setTimeout(() => this.connect(), 2_000);
      };
    } catch (err) {
      this.onError?.(String(err));
      this.reconnectTimer = setTimeout(() => this.connect(), 2_000);
    }
  }

  close(): void {
    this.closed = true;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.ws?.close();
    this.ws = null;
  }

  /** Send a JPEG-encoded keyframe. The canvas → blob → base64 conversion
   *  happens in the caller (which already has the video element handy);
   *  this method just frames + sends. */
  sendKeyframe(jpegBase64: string, positionClass: string | null): void {
    if (!this.enabled) return;
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    this.nextKeyframeId += 1;
    ws.send(
      JSON.stringify({
        type: 'keyframe',
        keyframe_id: `kf-${this.sessionId}-${this.nextKeyframeId}`,
        user_id: this.userId,
        session_id: this.sessionId,
        jpeg_b64: jpegBase64,
        position_class: positionClass,
      }),
    );
  }
}

/** Capture the current frame of an HTMLVideoElement as a base64 JPEG.
 *  Downsamples to maxWidth so the WS payload stays manageable. Returns
 *  null if the video isn't ready or capture fails. */
export function captureVideoFrameJpegBase64(
  video: HTMLVideoElement,
  maxWidth = 480,
  quality = 0.7,
): string | null {
  if (!video || video.readyState < 2 || video.videoWidth === 0) return null;
  const scale = Math.min(1, maxWidth / video.videoWidth);
  const w = Math.round(video.videoWidth * scale);
  const h = Math.round(video.videoHeight * scale);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(video, 0, 0, w, h);
  // toDataURL → strip the "data:image/jpeg;base64," prefix.
  const dataUrl = canvas.toDataURL('image/jpeg', quality);
  const comma = dataUrl.indexOf(',');
  return comma >= 0 ? dataUrl.slice(comma + 1) : null;
}
